import type { BrowserHostedSessionClient } from '@owlbert/core'
import { Button, Paper, Stack, Text, Textarea } from '@mantine/core'
import { IconSparkles } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import React from 'react'
import { generateAssistantProposalFromDescription, type AssistantProposalFields } from '../assistant/assistantProposalFromDescription'

export type AssistantAutoCreatePanelProps = {
  browserReady: boolean
  bundle: BrowserHostedSessionClient | null
  disabled?: boolean
  onProposal: (fields: AssistantProposalFields) => void
}

/**
 * Wizard-style step: describe the assistant in natural language, then generate structured fields.
 * Used on the hosted "New assistant" modal (Settings and Chats).
 */
export const AssistantAutoCreatePanel: React.FC<AssistantAutoCreatePanelProps> = ({
  browserReady,
  bundle,
  disabled = false,
  onProposal,
}) => {
  const [goalText, setGoalText] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const canUseAi = Boolean(browserReady && bundle?.client)

  const onGenerate = async () => {
    setError(null)
    if (!bundle?.client) {
      setError('Sign in with Owlbert cloud to use AI generation.')
      return
    }
    setLoading(true)
    try {
      const fields = await generateAssistantProposalFromDescription(bundle.client.llm, goalText)
      onProposal(fields)
      notifications.show({
        color: 'teal',
        message: 'Proposal ready — review the preview, then tap Create assistant.',
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not generate a proposal.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Paper withBorder p="sm" radius="md" bg="var(--mantine-color-body)">
      <Stack gap="sm">
        <div>
          <Text size="sm" fw={600}>
            Describe your assistant
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            Owlbert suggests a name, description, instructions, and greeting. Pick a model above, then create the
            assistant. Use the pencil icon in the list to adjust tools or prompts anytime.
          </Text>
        </div>
        <Textarea
          label="What should this assistant do for you?"
          placeholder="e.g. Help me draft weekly status emails for my team, keep a professional tone, and ask clarifying questions when context is missing."
          autosize
          minRows={3}
          maxRows={10}
          value={goalText}
          onChange={(e) => setGoalText(e.currentTarget.value)}
          disabled={disabled || loading}
        />
        {!canUseAi ? (
          <Text size="xs" c="dimmed">
            Sign in on Settings and select a workspace to enable AI-assisted setup.
          </Text>
        ) : null}
        {error ? (
          <Text size="xs" c="red">
            {error}
          </Text>
        ) : null}
        <Button
          variant="light"
          leftSection={<IconSparkles size={18} />}
          loading={loading}
          disabled={disabled || !goalText.trim() || !canUseAi}
          onClick={() => void onGenerate()}
        >
          Generate proposal
        </Button>
      </Stack>
    </Paper>
  )
}
